import { useEffect, useState } from 'react';
import clsx from 'clsx';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Select from 'react-select';
import { KTIcon } from '@/_metronic/helpers';
import { AnnouncementType } from '@/app/enums/AnnouncementType';
import { useDataTable } from '@/app/modules/table/DataTable';
import { useUrlFilter } from '@/app/modules/table/core/_helpers';
import {
  type AnnouncementListFilterValues,
  type AnnouncementStatusOption,
  type AnnouncementTypeOption,
} from '@/app/pages/notifications/announcements/core/_models';

const ANNOUNCEMENT_TYPE_OPTIONS: AnnouncementTypeOption[] = [
  { value: AnnouncementType.MANDATORY, label: 'Obrigatório' },
  { value: AnnouncementType.DISMISSIBLE, label: 'Dispensável' },
];

type AnnouncementListFilterProps = {
  statusOptions: AnnouncementStatusOption[];
};

const AnnouncementListFilter = ({ statusOptions }: AnnouncementListFilterProps) => {
  const { filter, setFilter, isLoading, table, tableKey } = useDataTable<AnnouncementListFilterValues>();
  const [urlFilter, setUrlFilter] = useUrlFilter<AnnouncementListFilterValues>(tableKey);
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState<AnnouncementListFilterValues>({});
  const activeFilterCount = Number(!!filter?.status) + Number(!!filter?.type);
  const hasActiveFilters = activeFilterCount > 0;
  const buttonLabel = hasActiveFilters ? `Filtros (${activeFilterCount})` : 'Filtrar';
  const titleId = `${tableKey}-filter-title`;
  const statusId = `${tableKey}-filter-status`;
  const typeId = `${tableKey}-filter-type`;
  const selectedStatus = statusOptions.find((option) => option.value === draft.status) ?? null;
  const selectedType = ANNOUNCEMENT_TYPE_OPTIONS.find((option) => option.value === draft.type) ?? null;

  useEffect(() => {
    const restoredFilters = urlFilter ?? {};
    setFilter(restoredFilters);
  }, [urlFilter, setFilter]);

  const openDrawer = () => {
    const appliedFilters = filter ?? {};
    setDraft(appliedFilters);
    setShow(true);
  };

  const applyFilters = (nextFilter: AnnouncementListFilterValues) => {
    const hasFilters = !!(nextFilter.status || nextFilter.type);
    const nextUrlFilter = hasFilters ? nextFilter : null;
    void setUrlFilter(nextUrlFilter);
    setFilter(nextFilter);
    table?.setPageIndex(0);
  };

  const clearFilters = () => {
    setDraft({});
    applyFilters({});
  };

  const confirmFilters = () => {
    applyFilters(draft);
    setShow(false);
  };

  return (
    <>
      <button
        aria-label={buttonLabel}
        className={clsx(
          'btn d-flex align-items-center position-relative',
          hasActiveFilters ? 'btn-primary' : 'btn-light-primary',
        )}
        disabled={isLoading}
        type="button"
        onClick={openDrawer}
      >
        <KTIcon className="fs-2" iconName="filter" />
        <span className="d-none d-sm-inline text-nowrap">{buttonLabel}</span>
        {hasActiveFilters ? (
          <span
            aria-hidden="true"
            className="position-absolute top-0 start-100 translate-middle badge badge-circle bg-warning d-sm-none"
          >
            {activeFilterCount}
          </span>
        ) : null}
      </button>
      <Offcanvas aria-labelledby={titleId} placement="end" show={show} onHide={() => setShow(false)}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-bolder" id={titleId}>
            Filtros
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <div className="flex-grow-1">
            <div className="mb-8">
              <label className="form-label fs-6 fw-bold" htmlFor={statusId}>
                Situação
              </label>
              <Select
                isClearable
                className="react-select-styled react-select-solid"
                classNamePrefix="react-select"
                inputId={statusId}
                options={statusOptions}
                placeholder="Todas as situações"
                value={selectedStatus}
                onChange={(option) => setDraft({ ...draft, status: option?.value, status_label: option?.label })}
              />
            </div>
            <div className="mb-8">
              <label className="form-label fs-6 fw-bold" htmlFor={typeId}>
                Tipo
              </label>
              <Select
                isClearable
                className="react-select-styled react-select-solid"
                classNamePrefix="react-select"
                inputId={typeId}
                options={ANNOUNCEMENT_TYPE_OPTIONS}
                placeholder="Todos os tipos"
                value={selectedType}
                onChange={(option) => setDraft({ ...draft, type: option?.value, type_label: option?.label })}
              />
            </div>
          </div>
          <div className="d-flex gap-3 pt-4 border-top">
            <button
              className="btn btn-light btn-active-light-primary fw-bold flex-grow-1"
              disabled={isLoading}
              type="button"
              onClick={clearFilters}
            >
              Limpar
            </button>
            <button
              className="btn btn-primary fw-bold flex-grow-1"
              disabled={isLoading}
              type="button"
              onClick={confirmFilters}
            >
              Aplicar
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AnnouncementListFilter;
